import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { authApi } from '../lib/api';
import { CheckCircle, AlertCircle, Loader2, Mail } from 'lucide-react';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, already_verified, error
  const [error, setError] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setError('Token no válido');
        return;
      }

      try {
        const response = await authApi.verifyEmail(token);
        if (response.data.already_verified) {
          setStatus('already_verified');
        } else {
          setStatus('success');
        }
      } catch (error) {
        setStatus('error');
        if (error.response?.data?.detail) {
          setError(error.response.data.detail);
        } else {
          setError('El enlace de verificación no es válido o ha expirado.');
        }
      }
    };

    verify();
  }, [token]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Verificando email...
            </CardTitle>
            <CardDescription>
              Por favor espera mientras verificamos tu email.
            </CardDescription>
          </>
        );
      case 'success':
        return (
          <>
            <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              ¡Email verificado!
            </CardTitle>
            <CardDescription>
              Tu email ha sido verificado correctamente. Ya puedes acceder a todas las funciones de OkPresu.
            </CardDescription>
          </>
        );
      case 'already_verified':
        return (
          <>
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Email ya verificado
            </CardTitle>
            <CardDescription>
              Tu email ya estaba verificado anteriormente.
            </CardDescription>
          </>
        );
      case 'error':
        return (
          <>
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Error de verificación
            </CardTitle>
            <CardDescription>
              {error}
            </CardDescription>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {renderContent()}
        </CardHeader>
        <CardContent>
          {status !== 'loading' && (
            <Link to="/dashboard">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="verify-goto-dashboard"
              >
                {status === 'success' || status === 'already_verified' 
                  ? 'Ir al Dashboard' 
                  : 'Intentar de nuevo'
                }
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
